import { Component } from '@theme/component';

const STORAGE_KEY = 'sa_utm_campaign_data';
const SESSION_KEY = 'sa_applied_discount_campaign';
const STORAGE_DURATION = 24 * 60 * 60 * 1000;

const DEFAULT_ANNOUNCEMENT_SELECTOR = '.eq-announcement-bar';

/**
 * Social announcement bar shown when its Ads Name matches utm_campaign.
 */
class EqSocialAnnouncementBar extends Component {
  /** @type {Set<number>} */
  #countdownIntervals = new Set();

  /** @type {HTMLElement[]} */
  #hiddenDefaultBars = [];

  connectedCallback() {
    super.connectedCallback();

    this.#initialize();
  }

  disconnectedCallback() {
    this.#clearCountdowns();
    this.#restoreDefaultAnnouncementBars();

    super.disconnectedCallback();
  }

  #initialize() {
    const utmCampaign = this.#getCampaign();

    if (!utmCampaign) return;

    const announcements = this.querySelectorAll(
      '.eq-social-announcement-bar__item[data-ads-name]'
    );

    announcements.forEach((announcement) => {
      if (!(announcement instanceof HTMLElement)) return;

      if (announcement.dataset.adsName !== utmCampaign) return;

      const discountCode =
        announcement.dataset.adsDiscountCode ?? '';

      const countdownTime =
        announcement.dataset.adsCountdownTime ?? '';

      announcement.hidden = false;

      this.#hideDefaultAnnouncementBars();

      this.#startCountdown(
        countdownTime,
        announcement
      );

      this.#applyDiscount(
        discountCode,
        utmCampaign
      );
    });
  }

  #getCampaign() {
    const now = Date.now();

    const url = new URL(window.location.href);

    const urlUtm =
      url.searchParams.get('utm_campaign');

    if (urlUtm) {
      this.#writeStorage(
        STORAGE_KEY,
        {
          value: urlUtm,
          expires:
            now +
            STORAGE_DURATION
        }
      );

      return urlUtm;
    }

    const stored =
      this.#readStorage(STORAGE_KEY);

    if (
      !stored ||
      typeof stored !== 'object'
    ) {
      return null;
    }

    if (
      typeof stored.expires !== 'number' ||
      stored.expires <= now
    ) {
      return null;
    }

    if (
      typeof stored.value !== 'string' ||
      stored.value === ''
    ) {
      return null;
    }

    return stored.value;
  }

  #hideDefaultAnnouncementBars() {
    document
        .querySelectorAll(DEFAULT_ANNOUNCEMENT_SELECTOR)
        .forEach((announcement) => {
        if (!(announcement instanceof HTMLElement)) {
            return;
        }

        const section = announcement.closest('.shopify-section');

        const target =
            section instanceof HTMLElement
            ? section
            : announcement;

        if (
            target.classList.contains(
            'eq-social-announcement-default-hidden'
            )
        ) {
            return;
        }

        target.classList.add(
            'eq-social-announcement-default-hidden'
        );

        this.#hiddenDefaultBars.push(target);
        });
    }

    #restoreDefaultAnnouncementBars() {
    this.#hiddenDefaultBars.forEach((element) => {
        element.classList.remove(
        'eq-social-announcement-default-hidden'
        );
    });

    this.#hiddenDefaultBars = [];
    }

  #applyDiscount(
    discountCode,
    utmCampaign
  ) {
    if (
      !discountCode ||
      discountCode.trim() === ''
    ) {
      return;
    }

    const alreadyApplied =
      this.#readSession(
        SESSION_KEY
      );

    if (
      alreadyApplied === utmCampaign ||
      window.location.pathname.startsWith(
        '/discount/'
      )
    ) {
      return;
    }

    this.#writeSession(
      SESSION_KEY,
      utmCampaign
    );

    // Without a readable session flag every page load would redirect again, looping forever.
    if (this.#readSession(SESSION_KEY) !== utmCampaign) {
      return;
    }

    const redirectUrl =
      window.location.pathname +
      window.location.search;

    window.location.href =
      `/discount/${encodeURIComponent(
        discountCode
      )}?redirect=${encodeURIComponent(
        redirectUrl
      )}`;
  }

  #startCountdown(
    countdownTime,
    container
  ) {
    if (
      !countdownTime ||
      container.hidden
    ) {
      return;
    }

    const targetTime =
      new Date(
        countdownTime
      ).getTime();

    if (
      Number.isNaN(targetTime)
    ) {
      return;
    }

    const timerDaysElement =
      container.querySelector(
        '[data-eq-social-timer-days]'
      );

    const timerHoursElement =
      container.querySelector(
        '[data-eq-social-timer-hours]'
      );

    const timerMinsElement =
      container.querySelector(
        '[data-eq-social-timer-mins]'
      );

    const updateCountdown = () => {
      if (container.hidden) {
        return false;
      }

      const distance =
        targetTime -
        Date.now();

      if (distance <= 0) {
        return false;
      }

      const days =
        Math.floor(
          distance /
            (
              1000 *
              60 *
              60 *
              24
            )
        );

      const hours =
        Math.floor(
          (
            distance %
            (
              1000 *
              60 *
              60 *
              24
            )
          ) /
            (
              1000 *
              60 *
              60
            )
        );

      const minutes =
        Math.floor(
          (
            distance %
            (
              1000 *
              60 *
              60
            )
          ) /
            (
              1000 *
              60
            )
        );

      this.#updateTimerPart(
        timerDaysElement,
        days,
        'day',
        'days'
      );

      this.#updateTimerPart(
        timerHoursElement,
        hours,
        'hour',
        'hours'
      );

      this.#updateTimerPart(
        timerMinsElement,
        minutes,
        'min',
        'mins'
      );

      return true;
    };

    if (!updateCountdown()) {
      return;
    }

    const interval =
      window.setInterval(() => {
        if (updateCountdown()) {
          return;
        }

        window.clearInterval(
          interval
        );

        this.#countdownIntervals.delete(
          interval
        );
      }, 1000);

    this.#countdownIntervals.add(
      interval
    );
  }

  #updateTimerPart(
    element,
    value,
    singular,
    plural
  ) {
    if (
      !(element instanceof HTMLElement)
    ) {
      return;
    }

    if (value > 0) {
      element.textContent =
        `${value} ${
          value > 1
            ? plural
            : singular
        } `;

      element.style.display =
        'inline';

      return;
    }

    element.style.display =
      'none';
  }

  #clearCountdowns() {
    this.#countdownIntervals.forEach(
      (interval) => {
        window.clearInterval(
          interval
        );
      }
    );

    this.#countdownIntervals.clear();
  }

  #readStorage(key) {
    try {
      const value =
        localStorage.getItem(key);

      return value
        ? JSON.parse(value)
        : null;
    } catch {
      return null;
    }
  }

  #writeStorage(
    key,
    value
  ) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify(value)
      );
    } catch {
      // Storage may be unavailable in restricted browser contexts.
    }
  }

  #readSession(key) {
    try {
      return sessionStorage.getItem(
        key
      );
    } catch {
      return null;
    }
  }

  #writeSession(
    key,
    value
  ) {
    try {
      sessionStorage.setItem(
        key,
        value
      );
    } catch {
      // Storage may be unavailable in restricted browser contexts.
    }
  }
}

if (
  !customElements.get(
    'eq-social-announcement-bar'
  )
) {
  customElements.define(
    'eq-social-announcement-bar',
    EqSocialAnnouncementBar
  );
}